package floris.graphics.landing.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.functions.LinearGradient
import com.varabyte.kobweb.compose.css.functions.linearGradient
import com.varabyte.kobweb.compose.dom.ref
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundImage
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.columnGap
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.pointerEvents
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import floris.graphics.core.navigation.Navigation
import floris.graphics.core.utils.pauseAllVideos
import com.materialdesignsystem.components.widgets.DsMaterialSymbols
import com.materialdesignsystem.components.widgets.DsVideo
import com.materialdesignsystem.components.widgets.FilledTonalIconButton
import com.materialdesignsystem.components.widgets.MaterialSymbolType
import com.materialdesignsystem.extensions.maxLines
import floris.graphics.models.domain.models.project.Project
import org.jetbrains.compose.web.css.div
import org.jetbrains.compose.web.css.minus
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.w3c.dom.HTMLVideoElement

@Composable
fun ProjectItem(
    project: Project,
    onClick: () -> Unit
) {
    val breakpoint = rememberBreakpoint()

    val playerId by remember { mutableStateOf("videoPlayer${project.id}") }

    var isPlaying by remember { mutableStateOf(false) }
    var showOverlay by remember(breakpoint) { mutableStateOf(breakpoint == Breakpoint.ZERO) }

    var videoElement: HTMLVideoElement? by remember { mutableStateOf(null) }

    LaunchedEffect(videoElement, breakpoint) {
        videoElement?.onplay = {
            isPlaying = true
            // Show overlay only when playing on non-mobile
            showOverlay = breakpoint != Breakpoint.ZERO
        }
        videoElement?.onpause = {
            isPlaying = false
            // Show overlay only when paused on mobile
            showOverlay = breakpoint == Breakpoint.ZERO
        }
    }

    fun playVideo() = videoElement?.play()?.catch { it.printStackTrace() }

    fun pauseVideo() = videoElement?.pause()

    Box(
        Modifier
            .fillMaxSize()
            .borderRadius(18.px)
            .overflow(Overflow.Clip)
            .thenIf(breakpoint != Breakpoint.ZERO) {
                Modifier
                    .pointerEvents(PointerEvents.Initial)
                    .onMouseEnter { playVideo() }
                    .onMouseLeave { pauseVideo() }
            },
        contentAlignment = Alignment.TopEnd
    ) {
        // TODO [TicketNumber]: When using Link or Anchor, returning back to the HomePage will result in a weird scroll position...
        A(
            href = Navigation.Screen.Project.getProject(project.id),
            attrs = Modifier
                .fillMaxSize()
                .textDecorationLine(TextDecorationLine.None)
                .onClick { onClick() }
                .toAttrs()
        ) {
            Box(modifier = Modifier.fillMaxSize()) {
                DsVideo(
                    videoUrl = project.previewVideoMediaFile?.url.toString(),
                    modifier = Modifier
                        .id(playerId)
                        .fillMaxSize()
                        .objectFit(ObjectFit.Cover)
                        .pointerEvents(PointerEvents.None),
                    poster = project.thumbnailMediaFile?.url.toString(),
                    loop = true,
                    controls = false,
                    // Get a reference to the underlying html element and their calls
                    // Look at this for more info: https://github.com/varabyte/kobweb?tab=readme-ov-file#elementrefscope-and-raw-html-elements
                    ref = ref { videoElement = it }
                ) {}

                if (showOverlay) {
                    ProjectItemOverlay(project = project)
                }
            }
        }

        if (breakpoint == Breakpoint.ZERO) {
            FilledTonalIconButton(
                modifier = Modifier.margin(16.px),
                onClick = {
                    pauseAllVideos()

                    val currentlyNotPlaying = !isPlaying
                    if (currentlyNotPlaying) playVideo() else pauseVideo()
                }
            ) {
                DsMaterialSymbols(
                    modifier = Modifier.fontSize(ButtonVars.Height.value() - ButtonVars.PaddingHorizontal.value().div(if (!isPlaying) 2 else 1)),
                    type= MaterialSymbolType.Rounded,
                    icon = if (!isPlaying) "play_arrow" else "pause"
                )
            }
        }
    }
}

@Composable
private fun ProjectItemOverlay(project: Project) {
    val breakpoint = rememberBreakpoint()

    Column(
        modifier = Modifier
            .padding(
                topBottom = if (breakpoint >= Breakpoint.MD) 24.px else 10.px,
                leftRight = if (breakpoint >= Breakpoint.MD) 24.px else 24.px
            )
            .fillMaxSize()
            .color(Colors.WhiteSmoke).backgroundImage(linearGradient(LinearGradient.Direction.ToTop) {
                add(Color.rgb(52, 52, 52).copyf(alpha = 0.9f), stop = 10.percent)
                add(Color.rgb(52, 52, 52).copyf(alpha = 0.8f), stop = 25.percent)
                add(Color.rgb(52, 52, 52).copyf(alpha = 0.0f), stop = 60.percent)
            }),
        verticalArrangement = Arrangement.Bottom
    ) {
        SpanText(
            modifier = Modifier
                .fillMaxWidth()
                .fontSize(22.px)
                .textOverflow(TextOverflow.Ellipsis)
                .maxLines(1)
                .lineHeight(28.px)
                .fontWeight(FontWeight.Bold),
            text = project.title
        )

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .columnGap(8.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                modifier = Modifier
                    .fillMaxWidth()
                    .fontSize(18.px)
                    .textOverflow(TextOverflow.Ellipsis)
                    .maxLines(1)
                    .lineHeight(30.px),
                text = project.subtitle
            )

            Row(
                modifier = Modifier
                    .columnGap(4.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                project.techstacks.orEmpty().take(4).forEach {
                    Image(
                        modifier = Modifier.size(20.px),
                        src = it.logoMediaFile?.url?.toString() ?: ""
                    )
                }

                if ((project.techstacks.orEmpty().size) > 4) {
                    SpanText(
                        modifier = Modifier
                            .margin(left = 4.px)
                            .fillMaxWidth()
                            .fontSize(18.px)
                            .fontWeight(FontWeight.Bold)
                            .lineHeight(24.px),
                        text = "+"
                    )
                }
            }
        }
    }
}
